import '@atlaskit/css-reset';
import React, { useCallback, useMemo, useState } from 'react';
import UFOSegment from '@atlaskit/react-ufo/segment';
import Suspense from '@atlaskit/react-ufo/suspense';
import { ModalContextProvider } from '@atlassian/entry-points/modal-context-provider';
import HeartbeatDisplay from '@atlassian/heartbeat/heartbeat';
import { PreloadedQuery, RelayEnvironmentProvider } from 'react-relay';
import { AkAnalyticsListeners } from '@townsquare/analytics/ak-listener';
import { AnalyticsClientImpl } from '@townsquare/analytics/client';
import { AnalyticsContext } from '@townsquare/analytics/context';
import { useAtlasKitAnalytics } from '@townsquare/analytics/use-ak-analytics';
import { AppContext } from '@townsquare/app-context';
import { useLoadAppsFromResource } from '@townsquare/apps-store/hooks';
import { AuthCheck } from '@townsquare/auth';
import { StorageAuthCheck } from '@townsquare/auth/storage-auth-check';
import { returnAfterLogin } from '@townsquare/auth-utils';
import { useBrowserStorageControls } from '@townsquare/auth-utils/use-browser-storage-controls';
import { useRovoWorkspace } from '@townsquare/collab-context';
import { CrossFlowProvider } from '@townsquare/crossflow/provider';
import { CrossFlowWAC } from '@townsquare/crossflow/wac';
import { Drawers } from '@townsquare/drawers';
import { setFavicon } from '@townsquare/dynamic-favicon';
import { Flags } from '@townsquare/flags/wrapper';
import { HelpContextProvider } from '@townsquare/help-context';
import { UserLocaleOverrideHandler } from '@townsquare/i18n/override';
import TownsquareIntlProvider from '@townsquare/i18n/provider';
import { Modals } from '@townsquare/modals/ui';
import { RouteListener } from '@townsquare/performance/route-listener';
import { PostOfficeContextProvider } from '@townsquare/post-office/post-office-provider';
import { initialiseAggRelayEnvironment } from '@townsquare/relay-utils/agg-environment';
import { getRelayEnvironment } from '@townsquare/relay-utils/townsquare-environment';
import { RouteBlocker } from '@townsquare/route-blocker';
import { CapabilityRouter } from '@townsquare/router/capability-router';
import { CapabilityRouterFromVolt } from '@townsquare/router/capability-router-from-volt';
import { createBrowserHistory } from '@townsquare/router/createBrowserHistory';
import { isEmbedRoute, matchRoute } from '@townsquare/router/primitives/match-route';
import { useRouteListeners } from '@townsquare/router/use-route-listeners';
import { useQueryParam } from '@townsquare/router/useQueryParam';
import { RovoChatSubscribers } from '@townsquare/rovo-chat/subscribers';
import { createSearchParamsFromCurrentUrl } from '@townsquare/search-params';
import { FullScreenSpaLoader } from '@townsquare/spa-loader';
import { fg } from '@townsquare/stat-sig/gate';
import { GlobalTheming } from '@townsquare/theme/global-theming';
import { AnalyticAttributeHandler } from './AnalyticsAttributeHandler';
import { Bootstrap, BootstrapResult } from './bootstrap/Bootstrap';
import { BootstrapFromVolt, BootstrapFromVoltResult } from './bootstrap/BootstrapFromVolt';
import { BootstrapProjectsQuery } from './bootstrap/__generated__/BootstrapProjectsQuery.graphql';
import { readBootstrapVoltPayload } from './bootstrap/voltPayload';
import { useKeyboardShortcuts } from './griffin-navigation/use-keyboard-shortcuts';
import { GriffinRoute } from './router/GriffinRoute';
import { useProjectsRoutes, useRoute } from './router/hooks';
import { routes } from './router/routes';
const analyticsClient = new AnalyticsClientImpl('web', 'townsquare');
const history = createBrowserHistory();
setFavicon('projects');
const EnforceCloudId = (props: {
  cloudId?: string;
}) => {
  const route = useRoute();
  const rovoWorkspaceCloudId = useRovoWorkspace();
  const cloudId = props.cloudId ?? rovoWorkspaceCloudId;
  const [qsCloudId, setQsCloudId] = useQueryParam('cloudId');
  if ('routeType' in route && route.routeType === 'global' && qsCloudId !== cloudId) {
    setQsCloudId(cloudId ?? undefined, 'replace');
  }
  return null;
};

/**
 * Discriminated union over the two bootstrap branches:
 * - `gql`: data came from the Relay-bound `Bootstrap` (preloaded query).
 *   `relayKey` is a `CapabilityRouterFragment$key` — pass to `<CapabilityRouter>`.
 * - `volt`: data came from the Volt JSON-inject `BootstrapFromVolt`.
 *   `workspaceVolt` is plain workspace data — pass to `<CapabilityRouterFromVolt>`.
 *
 * Every other field is identical between the two; the discriminant lets
 * `InsideBootstrap` mount the right CapabilityRouter without any other code
 * needing to care which branch fired.
 */
type InsideBootstrapData = ({
  source: 'gql';
} & BootstrapResult) | ({
  source: 'volt';
} & BootstrapFromVoltResult);
const InsideBootstrap = (data: InsideBootstrapData) => {
  // Compliance control
  useBrowserStorageControls();
  const projectsRoutes = useProjectsRoutes();
  const akAnalytics = useAtlasKitAnalytics();
  const isUGSHeadersMigrationEnabled = fg('townsquare_ugs_headers_migration');
  if (isUGSHeadersMigrationEnabled) {
    initialiseAggRelayEnvironment(data.collabContext?.rovoWorkspace ?? undefined);
  }
  useKeyboardShortcuts();
  useLoadAppsFromResource();
  if (!data.collabContext?.goalWorkspace) {
    // TODO: Show error message when workspace is missing
    return <div />;
  }
  const capabilityRouterChildren = <Suspense interactionName="projects-route-switcher" fallback={<FullScreenSpaLoader />}>
			<RouteListener />
			<RouteSwitcher />
		</Suspense>;
  const capabilityRouter = data.source === 'gql' ? <CapabilityRouter history={history} resourceContext={data} data={data.relayKey} routes={projectsRoutes}>
				{capabilityRouterChildren}
			</CapabilityRouter> : <CapabilityRouterFromVolt history={history} resourceContext={data} capabilities={data.workspaceVolt?.userCapabilities?.capabilities} routes={projectsRoutes}>
				{capabilityRouterChildren}
			</CapabilityRouterFromVolt>;
  return <RelayEnvironmentProvider environment={getRelayEnvironment(data.workspace?.cloudId ?? undefined)}>
			<CrossFlowProvider>
				<AkAnalyticsListeners client={akAnalytics}>
					<EnforceCloudId cloudId={data.workspace?.cloudId} />
					<AnalyticAttributeHandler />
					<UserLocaleOverrideHandler />
					<RovoChatSubscribers />
					<GlobalTheming />
					<HeartbeatDisplay onAuthenticationFailed={returnAfterLogin} />
					{capabilityRouter}
					<Modals />
					<Drawers />
					<Flags />
					<RouteBlocker />
				</AkAnalyticsListeners>
			</CrossFlowProvider>
		</RelayEnvironmentProvider>;
};
export function App(): React.JSX.Element {
  const urlSearch = Object.fromEntries(createSearchParamsFromCurrentUrl());
  const matchedRoute = matchRoute(routes, window.location.pathname, urlSearch);
  const AuthenticationCheck = isEmbedRoute(matchedRoute?.route) ? StorageAuthCheck : AuthCheck;

  // Bootstrap branch picker. Presence of the server-inlined payload on the
  // document IS the signal — the client has no gate of its own. Captured
  // once at mount; the global is set during document boot and is stable.
  const voltPayload = useMemo(() => readBootstrapVoltPayload(), []);
  const useVoltBranch = voltPayload != null;
  const [preloadedQueryRef, setPreloadedQueryRef] = useState<PreloadedQuery<BootstrapProjectsQuery> | null>(null);
  const onAuthDone = useCallback(() => {
    // Volt branch reads the inlined payload; no need for the Relay preload.
    if (useVoltBranch) {
      return;
    }
    import('./bootstrap/preload').then(({
      preloadedQueryRef
    }) => {
      setPreloadedQueryRef(preloadedQueryRef);
    });
  }, [useVoltBranch]);
  return <UFOSegment name="projects-app-root">
			<AuthenticationCheck onAuthDone={onAuthDone}>
				<AppContext.Provider value={{
        app: 'projects'
      }}>
					<TownsquareIntlProvider>
						<RelayEnvironmentProvider environment={getRelayEnvironment()}>
							<AnalyticsContext.Provider value={analyticsClient}>
								<PostOfficeContextProvider>
									<HelpContextProvider>
										<ModalContextProvider>
											{useVoltBranch ? <BootstrapFromVolt payload={voltPayload!} matchedRoute={matchedRoute} history={history}>
													{data => <InsideBootstrap source="volt" {...data} />}
												</BootstrapFromVolt> : preloadedQueryRef && <Bootstrap queryRef={preloadedQueryRef} matchedRoute={matchedRoute} history={history}>
														{data => <InsideBootstrap source="gql" {...data} />}
													</Bootstrap>}
										</ModalContextProvider>
									</HelpContextProvider>
								</PostOfficeContextProvider>
								{/* WAC via Start Cross Flow touch point support */}
								<CrossFlowWAC />
							</AnalyticsContext.Provider>
						</RelayEnvironmentProvider>
					</TownsquareIntlProvider>
				</AppContext.Provider>
			</AuthenticationCheck>
		</UFOSegment>;
}
function RouteSwitcher() {
  useRouteListeners(); // Required to init analytics
  return <GriffinRoute />;
}
export default App;